import { Connection, VersionedTransaction } from "@solana/web3.js";
import { NETWORK_CONFIG } from "./config";
import type { TransactionRequest, TransactionResponse, WalletAccount } from "./types";

export interface SolanaWalletAdapterLike {
  sendTransaction: (
    transaction: VersionedTransaction,
    connection: Connection,
    options?: {
      skipPreflight?: boolean;
      maxRetries?: number;
    },
  ) => Promise<string>;
}

function decodeBase64ToUint8Array(base64: string): Uint8Array {
  if (typeof atob === "function") {
    const binaryString = atob(base64);
    const length = binaryString.length;
    const bytes = new Uint8Array(length);

    for (let i = 0; i < length; i += 1) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    return bytes;
  }

  // Fallback for non-browser environments (e.g., tests)
  // eslint-disable-next-line no-undef
  return Uint8Array.from(Buffer.from(base64, "base64"));
}

/**
 * Sends a Solana transaction through a Solana wallet adapter–like provider.
 *
 * Assumptions:
 * - `tx.data` contains a base64-encoded serialized `VersionedTransaction`
 */
export async function sendSolanaTransaction(
  wallet: SolanaWalletAdapterLike,
  account: WalletAccount,
  tx: TransactionRequest,
): Promise<TransactionResponse> {
  if (!tx.data) {
    throw new Error("Missing serialized Solana transaction data");
  }

  const solanaNetwork = NETWORK_CONFIG.supportedNetworks.find(
    (network) =>
      network.chainNamespace === "solana" && network.caipNetworkId === account.caip2ChainId,
  );

  const rpcUrl = solanaNetwork?.rpcUrls?.default?.http?.[0];

  if (!rpcUrl) {
    throw new Error("Solana RPC URL not configured for selected network");
  }

  const connection = new Connection(rpcUrl, "confirmed");

  const serializedTx = decodeBase64ToUint8Array(tx.data);
  const transaction = VersionedTransaction.deserialize(serializedTx);

  const signature = await wallet.sendTransaction(transaction, connection, {
    skipPreflight: false,
  });

  return {
    hash: signature,
    chainId: account.chainId,
  };
}
